/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  EVENT_LOAD,
  EVENT_LOAD_FAIL,
  EVENT_LOAD_SUCCESS,
  EVENTS_FILTER_COUNTRY_VALUE_REMOVE,
  EVENTS_FILTER_PROVINCE_VALUE_REMOVE,
  EVENTS_FILTER_VALUE_ADD,
  EVENTS_FILTER_VALUE_REMOVE,
  EVENTS_FILTER_VALUE_UPDATE,
  EVENTS_RESET,
  EVENTS_SEARCH,
  EVENTS_SEARCH_FAIL,
  EVENTS_SEARCH_SUCCESS,
  UPDATE_EVENT_LISTING_CURRENT_PAGE,
  UPDATE_EVENT_LISTING_PAGE_SIZE,
  UPDATE_EVENT_STATUS,
  UPDATE_EVENT_STATUS_FAIL,
  UPDATE_EVENT_STATUS_SUCCESS,
  UPDATE_SELECTED_EVENT,
  UPDATE_SELECTED_EVENT_ID,
} from "../../Constants/ActionTypes";
import { IEvent } from "../../Interfaces/Event/IEvent";
import { IEventStatus } from "../../Interfaces/Event/IEventStatus";
import { IFilterValue } from "../../Interfaces/IFilterValue";

/**
 * Action for when loading an event.
 * @interface
 */
export interface ILoadEvent {
  type: typeof EVENT_LOAD;
  selectedEventId: string;
}

/**
 * Action for when an event is loaded successfully.
 * @interface
 */
export interface ILoadEventSuccess {
  type: typeof EVENT_LOAD_SUCCESS;
  event: IEvent;
}

/**
 * Action for when loading an event failed.
 * @interface
 */
export interface ILoadEventFail {
  type: typeof EVENT_LOAD_FAIL;
  loadSelectedEventError: string;
}

/**
 * Action for when adding selected filter value.
 * @interface
 */
export interface IEventsFilterValueAdd {
  type: typeof EVENTS_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IEventsFilterValueRemove {
  type: typeof EVENTS_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter province value.
 * @interface
 */
export interface IEventsFilterProvinceValueRemove {
  type: typeof EVENTS_FILTER_PROVINCE_VALUE_REMOVE;
}

/**
 * Action for when removing selected filter country value.
 * @interface
 */
export interface IEventsFilterCountryValueRemove {
  type: typeof EVENTS_FILTER_COUNTRY_VALUE_REMOVE;
}

/**
 * Action for when updating selected filter value.
 * @interface
 */
export interface IEventsFilterValueUpdate {
  type: typeof EVENTS_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Action for when searching events.
 * @interface
 */
export interface ISearchEvents {
  type: typeof EVENTS_SEARCH;
  searchText: string;
  selectedEventsFilterValues: IFilterValue[];
  searchPage: number;
  pageSize: number;
}

/**
 * Action for when events were searched successfully.
 * @interface
 */
export interface ISearchEventsSuccess {
  type: typeof EVENTS_SEARCH_SUCCESS;
  events: IEvent[];
  searchPage: number;
  pageSize: number;
  filteredCount: number;
}

/**
 * Action for when loading events failed.
 * @interface
 */
export interface ISearchEventsFail {
  type: typeof EVENTS_SEARCH_FAIL;
  error: string;
}

/**
 * Action to update the status of an event
 * @interface
 */
export interface IUpdateEventStatus {
  type: typeof UPDATE_EVENT_STATUS;
  eventStatus: IEventStatus;
}

/**
 * Action for when events were loaded successfully.
 * @interface
 */
export interface IUpdateEventStatusSuccess {
  type: typeof UPDATE_EVENT_STATUS_SUCCESS;
  event: IEvent;
}

/**
 * Action for when loading events failed.
 * @interface
 */
export interface IUpdateEventStatusFail {
  type: typeof UPDATE_EVENT_STATUS_FAIL;
  updateEventStatusError: string;
}

/**
 * Action for when updating user profile ID.
 * @interface
 */
export interface IUpdateSelectedEvent {
  type: typeof UPDATE_SELECTED_EVENT;
  selectedEvent: IEvent;
}

/**
 * Action for when setting the selected event id
 * @interface
 */
export interface IUpdateSelectedEventId {
  type: typeof UPDATE_SELECTED_EVENT_ID;
  selectedEventId: string;
}

/**
 * Action for when updating listing current page
 * @interface
 */
export interface IUpdateCurrentPage {
  type: typeof UPDATE_EVENT_LISTING_CURRENT_PAGE;
  currentPage: number;
}

/**
 * Action for when updating listing page size
 * @interface
 */
export interface IUpdatePageSize {
  type: typeof UPDATE_EVENT_LISTING_PAGE_SIZE;
  pageSize: number;
}

/**
 * Action for when resetting events
 * @interface
 */
export interface IEventsReset {
  type: typeof EVENTS_RESET;
}

/**
 * Action creator for EVENT_LOAD.
 */
export function loadEvent(selectedEventId: string): EventActionTypes {
  return {
    type: EVENT_LOAD,
    selectedEventId,
  };
}

/**
 * Action creator for EVENTS_LOAD_SUCCESS.
 * @param events the user profiles successfully loaded.
 */
export function loadEventSuccess(event: IEvent): EventActionTypes {
  return {
    type: EVENT_LOAD_SUCCESS,
    event,
  };
}

/**
 * Action creator for EVENTS_LOAD_FAIL.
 * @param error the error message from the API.
 */
export function loadEventFail(loadSelectedEventError: string): EventActionTypes {
  return {
    type: EVENT_LOAD_FAIL,
    loadSelectedEventError,
  };
}

/**
 * Action creator for EVENTS_FILTER_VALUE_ADD.
 * @param error the error message from the API.
 */
export function addEventsFilterValue(filterValue: IFilterValue): EventActionTypes {
  return {
    type: EVENTS_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for EVENTS_FILTER_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeEventsFilterValue(filterValue: IFilterValue): EventActionTypes {
  return {
    type: EVENTS_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for EVENTS_FILTER_PROVINCE_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeEventsFilterProvinceValue(): EventActionTypes {
  return {
    type: EVENTS_FILTER_PROVINCE_VALUE_REMOVE,
  };
}

/**
 * Action creator for EVENTS_FILTER_COUNTRY_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeEventsFilterCountryValue(): EventActionTypes {
  return {
    type: EVENTS_FILTER_COUNTRY_VALUE_REMOVE,
  };
}

/**
 * Action creator for EVENTS_FILTER_VALUE_UPDATE.
 * @param error the error message from the API.
 */
export function updateEventsFilterValue(filterValue: IFilterValue): EventActionTypes {
  return {
    type: EVENTS_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

/**
 * Action creator for EVENTS_SEARCH.
 */
export function searchEvents(
  searchText: string,
  selectedEventsFilterValues: IFilterValue[],
  searchPage: number,
  pageSize: number
): EventActionTypes {
  return {
    type: EVENTS_SEARCH,
    searchText,
    selectedEventsFilterValues,
    searchPage,
    pageSize,
  };
}

/**
 * Action creator for EVENTS_SEARCH_SUCCESS.
 * @param events the user profiles successfully searched.
 */
export function searchEventsSuccess(
  events: IEvent[],
  searchPage: number,
  pageSize: number,
  filteredCount: number
): EventActionTypes {
  return {
    type: EVENTS_SEARCH_SUCCESS,
    events,
    searchPage,
    pageSize,
    filteredCount,
  };
}

/**
 * Action creator for EVENTS_SEARCH_FAIL.
 * @param error the error message from the API.
 */
export function searchEventsFail(error: string): EventActionTypes {
  return {
    type: EVENTS_SEARCH_FAIL,
    error,
  };
}

/**
 * Action creator for UPDATE_EVENT_STATUS.
 */
export function updateEventStatus(eventStatus: IEventStatus): EventActionTypes {
  return {
    type: UPDATE_EVENT_STATUS,
    eventStatus,
  };
}

/**
 * Action creator for UPDATE_EVENT_STATUS_SUCCESS.
 * @param events the user profiles successfully loaded.
 */
export function updateEventStatusSuccess(event: IEvent): EventActionTypes {
  return {
    type: UPDATE_EVENT_STATUS_SUCCESS,
    event,
  };
}

/**
 * Action creator for UPDATE_EVENT_STATUS_FAIL.
 * @param error the error message from the API.
 */
export function updateEventStatusFail(updateEventStatusError: string): EventActionTypes {
  return {
    type: UPDATE_EVENT_STATUS_FAIL,
    updateEventStatusError,
  };
}

/**
 * Action creator for UPDATE_SELECTED_EVENT.
 */
export function updateSelectedEvent(selectedEvent: IEvent): EventActionTypes {
  return {
    type: UPDATE_SELECTED_EVENT,
    selectedEvent,
  };
}

/**
 * Action creator for UPDATE_SELECTED_EVENT_ID.
 */
export function updateSelectedEventId(selectedEventId: string): EventActionTypes {
  return {
    type: UPDATE_SELECTED_EVENT_ID,
    selectedEventId,
  };
}

/**
 * Action creator for UPDATE_EVENT_LISTING_CURRENT_PAGE.
 */
export function updateCurrentPage(currentPage: number): EventActionTypes {
  return {
    type: UPDATE_EVENT_LISTING_CURRENT_PAGE,
    currentPage,
  };
}

/**
 * Action creator for UPDATE_EVENT_LISTING_PAGE_SIZE.
 */
export function updatePageSize(pageSize: number): EventActionTypes {
  return {
    type: UPDATE_EVENT_LISTING_PAGE_SIZE,
    pageSize,
  };
}

/**
 * Action creator for EVENTS_RESET.
 * @returns {EventActionTypes}
 */
export function resetEvents(): EventActionTypes {
  return {
    type: EVENTS_RESET,
  };
}

export type EventActionTypes =
  | ILoadEvent
  | ILoadEventSuccess
  | ILoadEventFail
  | IEventsFilterValueAdd
  | IEventsFilterValueRemove
  | IEventsFilterProvinceValueRemove
  | IEventsFilterCountryValueRemove
  | IEventsFilterValueUpdate
  | ISearchEvents
  | ISearchEventsSuccess
  | ISearchEventsFail
  | IUpdateEventStatus
  | IUpdateEventStatusSuccess
  | IUpdateEventStatusFail
  | IUpdateSelectedEvent
  | IUpdateSelectedEventId
  | IUpdateCurrentPage
  | IUpdatePageSize
  | IEventsReset;
